import { defModule, type UpdateResult } from '@st/redux'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { deepEqual } from '@st/util/deep-equal'
import { AppRoute, oneHourCourseRoute, routes } from '~/features/routes'
import { Analytics, ErrorReporting, Routing } from '~/effects'
import { RouteLocation } from './routing/location-change'
import { upgradeLegacyId } from '~/util/legacy-id'
import { observeLocation } from '~/features/module-runtime'

export type RouteState = {
  location: RouteLocation
  route: AppRoute | null
}

export type RouteMessage =
  | { type: 'locationChanged'; location: RouteLocation }
  | { type: 'getStarted' }
  | { type: 'navigate'; pathname: string; replace?: boolean }
  | { type: 'redirectToUrl'; url: string }
  | { type: 'openUrl'; url: string }

const observeAppLocation = observeLocation(
  (location): RouteMessage => ({ type: 'locationChanged', location })
)

export const routeModule = defModule<{
  state: RouteState
  message: RouteMessage
  init: RouteLocation
}>({
  init: (initialLocation) => {
    const resolved = resolveRouteLocation(initialLocation)

    return [
      {
        location: resolved.location,
        route: resolved.route
      },
      Effect.gen(function* () {
        if (resolved.replacePath) {
          const routing = yield* Routing
          yield* routing.replacePath(resolved.replacePath)
        }

        yield* setRouteContext(resolved.previousRoute, resolved.location, resolved.route)
        return null
      })
    ] as const
  },
  update: handle,
  tasks: () => [observeAppLocation()]
})

function resolveRouteLocation(location: RouteLocation): {
  location: RouteLocation
  route: AppRoute | null
  previousRoute: AppRoute | null
  replacePath?: string
} {
  const initialRoute = routes.match(location.pathname) ?? null

  const replacePath = match(initialRoute)
    .with({ name: 'one_hour_course_legacy' }, () => oneHourCourseRoute({}))
    .with({ name: P.union('client_folder_legacy', 'organization_folder_legacy') }, (route) =>
      routes.toPath({
        organizationSlug: route.organizationSlug,
        name: match(route.name)
          .with('client_folder_legacy', () => 'client_folder' as const)
          .with('organization_folder_legacy', () => 'organization_folder' as const)
          .exhaustive(),
        folderId: upgradeLegacyId(route.folderId)
      })
    )
    .otherwise(() => undefined)

  return match(replacePath)
    .with(P.string, (pathname) => ({
      location: { ...location, pathname },
      route: routes.match(pathname) ?? null,
      previousRoute: initialRoute,
      replacePath: pathname
    }))
    .otherwise(() => ({ location, route: initialRoute, previousRoute: null }))
}

function getFolderIdFromRoute(route: AppRoute | null): string | undefined {
  if (!route) {
    return undefined
  }
  return 'folderId' in route ? (route as { folderId: string }).folderId : undefined
}

function viewedEventForRoute(route: AppRoute | null) {
  switch (route?.name) {
    case 'sign_up':
      return 'viewed_sign_up_page' as const
    case 'branding':
      return 'viewed_branding_page' as const
    case 'logged_out_pricing':
      return 'viewed_plans_page' as const
    default:
      return undefined
  }
}

function setRouteContext(
  prevRoute: AppRoute | null,
  location: RouteLocation,
  route: AppRoute | null
) {
  const viewedEvent = viewedEventForRoute(route)
  const shouldTrackViewed = !!viewedEvent && prevRoute?.name != route?.name

  return Effect.gen(function* () {
    const analytics = yield* Analytics
    const errorReporting = yield* ErrorReporting
    yield* errorReporting.setTags({
      route: route?.name,
      folder_id: getFolderIdFromRoute(route)
    })
    yield* analytics.trackLocationChanged(location.pathname)

    if (viewedEvent && shouldTrackViewed) {
      yield* analytics.track(viewedEvent)
    }

    return null
  })
}

function handle(state: RouteState, event: RouteMessage): UpdateResult<RouteState, RouteMessage> {
  switch (event.type) {
    case 'locationChanged': {
      const resolved = resolveRouteLocation(event.location)
      const next = {
        ...state,
        location: resolved.location,
        route: resolved.route
      }

      if (deepEqual(state.location, next.location) && deepEqual(state.route, next.route)) {
        return state
      }

      return [
        next,
        Effect.gen(function* () {
          if (resolved.replacePath) {
            const routing = yield* Routing
            yield* routing.replacePath(resolved.replacePath)
          }

          yield* setRouteContext(state.route, next.location, next.route)
          return null
        })
      ] as const
    }
    case 'getStarted': {
      return [
        state,
        Effect.gen(function* () {
          const analytics = yield* Analytics
          const routing = yield* Routing
          yield* analytics.track('clicked_get_started')
          yield* routing.pushPath('/sign-up')
          return null
        })
      ] as const
    }
    case 'navigate': {
      return [
        state,
        Effect.gen(function* () {
          const routing = yield* Routing
          if (event.replace) {
            yield* routing.replacePath(event.pathname)
          } else {
            yield* routing.pushPath(event.pathname)
          }
          return null
        })
      ] as const
    }
    case 'redirectToUrl': {
      return [
        state,
        Effect.gen(function* () {
          const routing = yield* Routing
          yield* routing.redirectTo(event.url)
          return null
        })
      ] as const
    }
    case 'openUrl': {
      return [
        state,
        Effect.gen(function* () {
          const routing = yield* Routing
          yield* routing.openUrl(event.url)
          return null
        })
      ] as const
    }
  }
}

export function selRoute(state: RouteState) {
  if (!state.route) {
    throw new Error(`No route match for ${state.location.pathname}`)
  }
  return state.route
}
